import React, { createContext, useContext, ReactNode } from 'react';
import { toast } from 'react-toastify';

interface FeedbackToastContextType {
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  showLoading: (message: string) => void;
  dismissToast: (toastId: string | number) => void;
}

const FeedbackToastContext = createContext<FeedbackToastContextType | undefined>(undefined);

interface FeedbackToastProviderProps {
  children: ReactNode;
}

export const FeedbackToastProvider: React.FC<FeedbackToastProviderProps> = ({ children }) => {
  const showSuccess = (message: string) => {
    toast.success(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showError = (message: string) => {
    toast.error(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showWarning = (message: string) => {
    toast.warning(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showInfo = (message: string) => {
    toast.info(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const showLoading = (message: string) => {
    return toast.loading(message, {
      position: 'top-right',
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
    });
  };

  const dismissToast = (toastId: string | number) => {
    toast.dismiss(toastId);
  };

  const value: FeedbackToastContextType = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    dismissToast,
  };

  return (
    <FeedbackToastContext.Provider value={value}>
      {children}
    </FeedbackToastContext.Provider>
  );
};

export const useFeedbackToast = (): FeedbackToastContextType => {
  const context = useContext(FeedbackToastContext);
  if (context === undefined) {
    throw new Error('useFeedbackToast must be used within a FeedbackToastProvider');
  }
  return context;
}; 